/**
 * mSTEM EXT-02 — bridge user-declared blocks (mstem.customBlocks in localStorage)
 * into a REAL, runtime-registered openblock-vm extension so they show up in the
 * Scratch palette and can be dragged into the workspace.
 *
 * Scope (per user): blocks only need to APPEAR in the palette, be DRAGGABLE, and
 * PERSIST. They do not need meaningful run behaviour — every opcode is a safe
 * no-op (COMMAND does nothing, REPORTER returns '', BOOLEAN returns false) so a
 * running project never throws.
 *
 * HOW IT WIRES (verified against openblock-vm src):
 *   registerOrRefreshCustomBlocks(vm)
 *     └─ first time:  vm.extensionManager._registerInternalExtension(instance)
 *          → registerExtensionServiceSync → getInfo() → runtime._registerExtensionPrimitives
 *          → push into runtime._blockInfo + _fillExtensionCategory (_primitives/_hats)
 *          → emit SCRATCH_EXTENSION_ADDED  → blocks.jsx refreshes the toolbox.
 *     └─ later (add/edit/delete): vm.extensionManager.refreshBlocks()
 *          → runtime._refreshExtensionPrimitives → emit BLOCKSINFO_UPDATE
 *          → blocks.jsx re-renders the palette. No page reload.
 *
 * CRITICAL (openblock caveat, discovered in scratch3_mbf_ai): the extension
 * object MUST expose a getter `EXTENSION_ID`. Without it, _registerInternalExtension
 * builds a `device_*` service name and the category lands in _deviceBlockInfo
 * (hidden from the deviceless palette) instead of _blockInfo. mbfRobot/mbfSmartHome
 * omit it (they ride the device flow); mbfAI declares it — we follow mbfAI.
 *
 * All strings VN by default (project convention).
 */
import {readCustomBlocks} from './mstem-extension-storage.js';

// Single category that collects every user-declared block. `id` must match the
// VM regex /^[a-z0-9]+$/i (no dashes/underscores/spaces) — runtime.js:669.
const EXTENSION_ID = 'mstemblocks';
const CATEGORY_NAME = '🧩 Khối của tôi';
const DEFAULT_COLOR1 = '#7B5CFF';
const DEFAULT_COLOR2 = '#6A4CE0';
const DEFAULT_COLOR3 = '#5A3EC0';

// Block-builder shape → openblock BlockType string value.
// (BlockType: COMMAND:'command', REPORTER:'reporter', BOOLEAN:'Boolean', HAT:'hat')
const SHAPE_TO_BLOCK_TYPE = {
    command: 'command',
    reporter: 'reporter',
    boolean: 'Boolean',
    hat: 'hat'
};

// Block-builder input type → openblock ArgumentType string value.
// (ArgumentType: NUMBER:'number', STRING:'string', BOOLEAN:'Boolean')
// `menu` has no user-supplied item list in the builder, so it degrades to a
// plain string input for now.
const INPUT_TYPE_TO_ARG_TYPE = {
    number: 'number',
    text: 'string',
    boolean: 'Boolean',
    menu: 'string'
};

/**
 * Build a unique, VM-safe opcode from a stored block record's id.
 * generateId() yields alphanumerics; the `block-` prefix contributes a dash we
 * strip. The leading 'blk' guarantees the opcode starts with a letter.
 * @param {object} block - a mstem.customBlocks record.
 * @returns {string} an alphanumeric opcode unique to this block.
 */
const opcodeFor = block => `blk${String((block && block.id) || '').replace(/[^a-zA-Z0-9]/g, '')}`;

/**
 * Turn a stored block record into a getInfo() block's {text, arguments}.
 * Each declared input becomes an ARG{i} slot. If the user typed the input's
 * name as a [placeholder] inside the block name, we substitute in place;
 * otherwise the slot is appended. Any leftover [brackets] that don't map to a
 * real ARG are de-bracketed so the VM placeholder parser can't choke on them.
 * @param {object} block - a mstem.customBlocks record.
 * @returns {{text: string, args: object}} scratch-blocks text + arguments map.
 */
const buildTextAndArgs = block => {
    const inputs = Array.isArray(block.inputs) ? block.inputs : [];
    let text = ((block && block.name) || '').trim() || 'khối';
    const args = {};
    inputs.forEach((input, i) => {
        const key = `ARG${i}`;
        const argType = INPUT_TYPE_TO_ARG_TYPE[input && input.type] || 'string';
        const arg = {type: argType};
        if (argType === 'number') arg.defaultValue = 0;
        else if (argType === 'string') arg.defaultValue = '';
        // Boolean args are a hexagonal slot — no defaultValue.
        args[key] = arg;

        const placeholder = input && input.name && input.name.trim();
        let placed = false;
        if (placeholder) {
            const token = `[${placeholder}]`;
            if (text.indexOf(token) !== -1) {
                text = text.split(token).join(`[${key}]`);
                placed = true;
            }
        }
        if (!placed) {
            text += ` [${key}]`;
        }
    });
    // Strip any [foo] that isn't one of our [ARGn] slots so the VM doesn't try
    // to resolve it as a missing argument.
    text = text.replace(/\[(?!ARG\d+\])[^[\]]*\]/g, match => match.slice(1, -1));
    return {text, args};
};

/**
 * Safe no-op opcode handler for a given block shape. Blocks only need to exist
 * and be draggable; running one must never throw.
 * @param {string} shape - the block-builder shape ('command'|'reporter'|'boolean'|'hat').
 * @returns {Function} an (args, util) => value handler.
 */
const makeHandler = shape => {
    if (shape === 'reporter') return () => '';
    if (shape === 'boolean') return () => false;
    return () => {}; // command / hat — do nothing
};

/**
 * Factory for the runtime extension class. getInfo() reads the current
 * mstem.customBlocks on every call (so refreshBlocks() picks up adds/edits/
 * deletes) AND lazily attaches an opcode method for each block — the method
 * must exist on the instance when the VM prepares block info right after
 * getInfo() returns (extension-manager.js:812-833).
 * @returns {Function} the extension class.
 */
const makeCustomBlocksExtension = () => {
    class MstemCustomBlocks {
        constructor (runtime) {
            this.runtime = runtime;
        }
        // REQUIRED: registers this as a (deviceless) scratch extension → palette.
        get EXTENSION_ID () {
            return EXTENSION_ID;
        }
        getInfo () {
            const stored = readCustomBlocks();
            const blocks = stored.map(block => {
                const opcode = opcodeFor(block);
                const {text, args} = buildTextAndArgs(block);
                // Attach the opcode handler so serviceObject[opcode] resolves.
                if (typeof this[opcode] !== 'function') {
                    this[opcode] = makeHandler(block.shape);
                }
                return {
                    opcode,
                    blockType: SHAPE_TO_BLOCK_TYPE[block.shape] || 'command',
                    text,
                    arguments: args
                };
            });
            // Category colour follows the first block's colour when available so
            // the palette reflects the user's choice (Scratch renders one colour
            // per category, not per block).
            const first = stored[0];
            const color1 = (first && first.color) || DEFAULT_COLOR1;
            return [{
                id: EXTENSION_ID,
                name: CATEGORY_NAME,
                color1: color1,
                color2: DEFAULT_COLOR2,
                color3: DEFAULT_COLOR3,
                blocks: blocks
            }];
        }
    }
    return MstemCustomBlocks;
};

/**
 * Register the user-declared-blocks extension into the VM, or refresh it if it
 * is already loaded. Safe to call repeatedly (boot, save, delete).
 *   - Not yet loaded + at least one block  → _registerInternalExtension(...)
 *   - Already loaded                        → refreshBlocks()
 *   - Not yet loaded + zero blocks          → nothing (no empty category)
 * @param {object} vm - the openblock VirtualMachine instance.
 * @returns {void}
 */
const registerOrRefreshCustomBlocks = vm => {
    if (!vm || !vm.extensionManager || !vm.runtime) return;
    const em = vm.extensionManager;
    const alreadyLoaded = typeof em.isExtensionLoaded === 'function' &&
        em.isExtensionLoaded(EXTENSION_ID);

    if (alreadyLoaded) {
        // Re-reads getInfo() for every loaded extension → picks up add/edit/delete.
        if (typeof em.refreshBlocks === 'function') em.refreshBlocks();
        return;
    }

    // Don't register an empty category on first boot with no saved blocks.
    if (!readCustomBlocks().length) return;

    const Extension = makeCustomBlocksExtension();
    const instance = new Extension(vm.runtime);
    const serviceName = em._registerInternalExtension(instance);
    // Mirror what loadExtensionURL does for builtins so the VM treats this as a
    // properly loaded extension (isExtensionLoaded, project save/reload).
    if (em._loadedExtensions && typeof em._loadedExtensions.set === 'function') {
        em._loadedExtensions.set(EXTENSION_ID, serviceName);
    }
    if (typeof vm.runtime.addScratchExtension === 'function') {
        vm.runtime.addScratchExtension(EXTENSION_ID);
    }
};

export {
    registerOrRefreshCustomBlocks,
    EXTENSION_ID,
    opcodeFor,
    buildTextAndArgs
};
